import { type MarketDataSnapshot, type MarketDataSnapshotLevel, SideEnum } from '@talos/kyoko';
import Big from 'big.js';
import type { OwnOrderResult } from '../../hooks/useOwnOrders';
import type { OrderBookSnapshotLevel } from './types';

const EMPTY_ORDER_BOOK_LEVEL: OrderBookSnapshotLevel = {
  Price: '0',
  Size: '0',
  CumQty: '0',
};

/**
 * Returns the appropriate price field based on pricing mode.
 * When isPricingModeBPS is true, returns the BPS field; otherwise returns Price.
 */
export function getLevelPrice(level: MarketDataSnapshotLevel | undefined, isPricingModeBPS?: boolean): string {
  if (!level) {
    return '0';
  }
  return (isPricingModeBPS ? level.BPS : level.Price) ?? '0';
}

export function getOrderBookLevels(snapshot: MarketDataSnapshot, depth: number) {
  const offers: OrderBookSnapshotLevel[] = snapshot.Offers.slice(0, depth);
  const bids: OrderBookSnapshotLevel[] = snapshot.Bids.slice(0, depth);

  let offersSizeSum = Big(0);
  let bidsSizeSum = Big(0);
  for (let level = 0; level < depth; level++) {
    if (offers.length <= level) {
      offers.push(EMPTY_ORDER_BOOK_LEVEL);
    }
    offersSizeSum = offersSizeSum.add(offers[level]?.Size ?? '0');
    offers[level].CumQty = offersSizeSum;
    if (bids.length <= level) {
      bids.push(EMPTY_ORDER_BOOK_LEVEL);
    }
    bidsSizeSum = bidsSizeSum.add(bids[level]?.Size ?? '0');
    bids[level].CumQty = bidsSizeSum;
  }
  return { offers, bids, offersSizeSum: offersSizeSum.toNumber(), bidsSizeSum: bidsSizeSum.toNumber() };
}

export function spreadBps(priceA: string, priceB: string, side: SideEnum, pricesInBPS = false): string {
  const a = Big(priceA || 0);
  const b = Big(priceB || 0);

  if (b.eq(0)) {
    return '0';
  }

  const diff = side === SideEnum.Buy ? b.minus(a) : a.minus(b);
  // When prices are already in BPS, just return the difference.
  // Otherwise, convert to BPS by dividing and multiplying by 10000.
  const result = pricesInBPS ? diff.toFixed(1) : diff.div(b).times(10000).toFixed(1);
  // do not show -0.0 because of rounding
  return Big(result).eq(0) ? Big(0).toFixed(1) : result;
}

export function getOwnOrdersByPrice(orders: OwnOrderResult[]) {
  return orders.reduce((acc, order) => {
    const key = order.price;

    if (!key) {
      return acc;
    }
    const items = acc.get(key) || [];
    items.push(order);
    acc.set(key, items);
    return acc;
  }, new Map<string, OwnOrderResult[]>());
}
